import type { AvailablePlans, Plan } from '@/types';
import { brandPlanName } from '@/lib/brand';

export type CreditsMetadata = {
  count?: number;
  id?: string;
  name?: string;
};

export type CreditStatus = 'good' | 'warning' | 'critical' | 'unknown';

export const KNOWN_TIER_TOTALS: Record<string, number> = {
  // ─── Legacy ─── kept so grandfathered customers' credit usage still
  // resolves a total without depending on the new V2 plan names.
  'Free Tier': 30,
  'Starter Tier I': 500,
  'Starter Tier II': 1000,
  'Starter Tier III': 1500,
  'Pro Tier I': 2000,
  'Pro Tier II': 3500,
  'Pro Tier III': 5000,
  'Enterprise Tier I': 10000,
  'Enterprise Tier II': 20000,
  'Enterprise Tier III': 50000,

  // ─── V2 ─── Stripe product names exactly. Names embed the credit
  // count so multiple plans under one product map to distinct keys.
  // Long-term: `parseCreditsFromFeature` already extracts the count
  // from the plan's `features` array, so this hard-coded map can be
  // deleted once every V2 product has its features wired in Stripe.
  'Cleanlist Free': 30,
  'Cleanlist Starter (1,500)': 1500,
  'Cleanlist Starter (3,000)': 3000,
  'Cleanlist Pro (5,000)': 5000,
  'Cleanlist Pro (7,500)': 7500,
  'Cleanlist Pro (10,000)': 10000,
  'Cleanlist Scale (15,000)': 15000,
  'Cleanlist Scale (20,000)': 20000,
  'Cleanlist Scale (50,000)': 50000,
  'Cleanlist Scale (100,000)': 100000,
};

const CREDIT_FEATURE_REGEX = /(\d[\d,]*\.?\d*)\s*(k)?\s*credits?/i;
const MONTHLY_HINT_REGEX = /(per month|\/month|\/mo|monthly)/i;

const parseCreditsFromFeature = (feature: string): number | null => {
  const match = feature.match(CREDIT_FEATURE_REGEX);
  if (!match) return null;

  const rawNumber = match[1].replace(/,/g, '');
  const base = Number(rawNumber);
  if (Number.isNaN(base)) return null;

  return match[2] ? base * 1000 : base;
};

// A zero is copy, not an allowance ("Email sends at 0 credits").
const isAllowance = (value: number | null): value is number =>
  value !== null && value > 0;

const findPlanCredits = (plan: Plan | null | undefined): number | null => {
  if (!plan?.features?.length) return null;

  const monthlyCandidates = plan.features
    .filter((feature) => MONTHLY_HINT_REGEX.test(feature))
    .map(parseCreditsFromFeature)
    .filter(isAllowance);

  if (monthlyCandidates.length) {
    return Math.max(...monthlyCandidates);
  }

  const genericCandidates = plan.features
    .map(parseCreditsFromFeature)
    .filter(isAllowance);

  if (!genericCandidates.length) return null;

  return Math.max(...genericCandidates);
};

/**
 * Single source of truth for a plan's monthly credit total.
 *
 * Prefers the count parsed from the plan's live Stripe `features`
 * (`findPlanCredits`) and falls back to the hard-coded `KNOWN_TIER_TOTALS`
 * map keyed by plan name — the same precedence `getCreditsUsage` uses
 * internally. Display sites (navbar, plans page, credit dropdown, tier
 * cards) MUST call this rather than reading `KNOWN_TIER_TOTALS` directly,
 * so every surface agrees on the denominator (the hard-coded map silently
 * misses legacy plans whose names aren't listed; the parsed value covers
 * them). Returns 0 when neither source resolves.
 */
export const getPlanCredits = (plan: Plan | null | undefined): number =>
  plan?.credits_count ??
  findPlanCredits(plan) ??
  (plan?.name ? (KNOWN_TIER_TOTALS[plan.name] ?? 0) : 0);

/**
 * True when the org is still on a pre-v4 paid plan while being shown the v4
 * catalog, whose tiers reuse the old names (Starter, Pro) at new prices and
 * credits. Free and custom contracts are left alone.
 */
export const isOnLegacyPlan = (
  catalog: AvailablePlans | undefined
): boolean => {
  const current = catalog?.current_plan;
  return (
    catalog?.pricing_version === 'v4' &&
    catalog.catalog_family !== 'custom' &&
    !!current &&
    current.pricing_version !== 'v4' &&
    current.tier_key !== 'free' &&
    !/^free\b/i.test(current.name)
  );
};

/** The current plan's name, marked "(legacy)" when `isOnLegacyPlan`. */
export const currentPlanName = (
  catalog: AvailablePlans | undefined
): string | undefined => {
  const name = brandPlanName(catalog?.current_plan?.name);
  return name && isOnLegacyPlan(catalog) ? `${name} (legacy)` : name;
};

/** Seats × credits per seat, plus the add-on once. Must match the backend
 *  grant: `pricing_seats × tier credits + pricing_credit_addon`. */
const perSeatMonthlyCredits = (
  plan: Plan | null,
  catalog: AvailablePlans | undefined
): number | null => {
  const perSeat = plan?.credits_per_seat ?? 0;
  if (!plan || perSeat <= 0) return null;
  const current = catalog?.current_plan;
  // Every v4 add-on size shares one per-credit price, so its id can't say
  // which size it is: the backend sends the size. The id names a legacy
  // per-size add-on.
  const addonId = current?.credit_addon_price_id;
  const addon =
    current?.credit_addon_credits ??
    (addonId
      ? (plan.credit_options?.find(
          (option) =>
            option.monthly_price_id === addonId ||
            option.yearly_price_id === addonId
        )?.credits ?? 0)
      : 0);
  return Math.max(1, catalog?.seats_paid ?? 1) * perSeat + addon;
};

const findPlanMatch = (
  availablePlans: AvailablePlans | undefined,
  creditsMetadata: CreditsMetadata | undefined
): Plan | null => {
  if (!availablePlans) return null;

  const plans = availablePlans.plans ?? [];
  const byId = creditsMetadata?.id
    ? plans.find((plan) => plan.id === creditsMetadata.id)
    : null;
  if (byId) return byId;

  const byName = creditsMetadata?.name
    ? plans.find((plan) => plan.name === creditsMetadata.name)
    : null;
  if (byName) return byName;

  return availablePlans.current_plan ?? null;
};

/**
 * A credit balance as people read it: grouped thousands, one decimal where the
 * balance is fractional, none where it is whole. Never rounded up, so the
 * sidebar and the credits menu agree with the ledger to the tenth. A balance
 * not known (still loading, or the read failed) is a dash, never 0.
 */
export const formatCredits = (value: number | null): string => {
  if (value === null) return '—';
  // Down to the tenth: 0.96 reads 0.9, never 1, or the not-enough-credits
  // dialog says "you have 1" for an action that costs 1. Rounding to the
  // thousandth first drops float noise (2.3 * 10 is 22.999...). `|| 0` folds
  // -0 (a tiny negative balance) into 0 so it never reads "-0".
  const tenths = Math.trunc(Math.round(value * 1000) / 100) / 10 || 0;
  return tenths.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
};

export const getCreditsCount = (
  creditsMetadata: CreditsMetadata | undefined
): number => {
  const count = creditsMetadata?.count;
  return typeof count === 'number' && Number.isFinite(count) ? count : 0;
};

export const getCreditsUsage = (
  availablePlans: AvailablePlans | undefined,
  creditsMetadata: CreditsMetadata | undefined
) => {
  // No balance read (loading, or the request failed) is unknown, not an
  // empty, critical meter.
  if (!creditsMetadata)
    return {
      availableCredits: 0,
      totalCredits: null,
      percentage: null,
      status: 'unknown' as CreditStatus,
    };
  const availableCredits = getCreditsCount(creditsMetadata);

  // AppSumo + stacking. Two regimes:
  //
  //   1. AppSumo-only (no Stripe sub, or Stripe sub on Free):
  //      denominator is the AppSumo monthly grant. Without this
  //      short-circuit the dropdown shows "Free / 30 credits"
  //      because Stripe ``current_plan`` is null and no
  //      ``KNOWN_TIER_TOTALS`` entry matches.
  //
  //   2. Stacked (AppSumo + a paid Cleanlist plan, tier_key !==
  //      "free"): both grants pour into the same ``credits_count``
  //      pool on their own cycles, so the denominator must sum
  //      both monthly allowances. Without this, a stacked
  //      Tier 1 + Pro 5,000 user would see "X / 150" and the
  //      progress bar would saturate at 5% of their actual
  //      allowance.
  if (availablePlans?.appsumo) {
    const appsumo = availablePlans.appsumo;
    const currentPlan = availablePlans.current_plan;
    const isStacked = !!(
      currentPlan &&
      currentPlan.tier_key &&
      currentPlan.tier_key !== 'free'
    );
    const stripeGrant = isStacked ? (findPlanCredits(currentPlan) ?? 0) : 0;
    const totalCredits = appsumo.monthly_credits + stripeGrant;
    const percentage =
      totalCredits > 0
        ? Math.max(0, Math.min(1, availableCredits / totalCredits))
        : null;
    let status: CreditStatus = 'unknown';
    if (percentage !== null) {
      if (percentage <= 0.2) status = 'critical';
      else if (percentage <= 0.5) status = 'warning';
      else status = 'good';
    } else if (availableCredits <= 0) {
      status = 'critical';
    }
    return { availableCredits, totalCredits, percentage, status };
  }

  const matchedPlan = findPlanMatch(availablePlans, creditsMetadata);
  const totalFromPlan =
    perSeatMonthlyCredits(matchedPlan, availablePlans) ??
    findPlanCredits(matchedPlan);
  const totalFromTierName = creditsMetadata?.name
    ? KNOWN_TIER_TOTALS[creditsMetadata.name]
    : null;
  const totalCredits = totalFromPlan ?? totalFromTierName ?? null;

  const percentage =
    totalCredits && totalCredits > 0
      ? Math.max(0, Math.min(1, availableCredits / totalCredits))
      : null;

  let status: CreditStatus = 'unknown';
  if (percentage !== null) {
    if (percentage <= 0.2) {
      status = 'critical';
    } else if (percentage <= 0.5) {
      status = 'warning';
    } else {
      status = 'good';
    }
  } else if (availableCredits <= 0) {
    status = 'critical';
  }

  return {
    availableCredits,
    totalCredits,
    percentage,
    status,
  };
};
